import { useEffect, useRef, type ReactNode, type RefObject } from 'react';

const FOKUSBAR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Panelets ene dialog (UX-gjennomgangen 2026-09-14, A10/B2). Fire ting som bare
 * `Tastaturkart` og `RettetDialog` gjorde hver for seg, samlet:
 *
 * - **Fokus inn** ved åpning: `fokusFørst` når kalleren peker på et felt eller den trygge
 *   knappen, ellers det første fokuserbare barnet. En skjermleser skal høre dialogens
 *   innhold, ikke «Lukk».
 * - **Fokusfelle**: Tab fra siste går til første, Shift+Tab fra første til siste. Siden bak
 *   ligger i DOM-en, og uten fella vandrer Tab gjennom hele den før den når dialogen.
 * - **Esc lukker**, og når aldri siden bak: der ville den også lukket skjemapanelet på
 *   Programmer og Kjente feil.
 * - **Sidens taster blokkeres.** Sidene lytter på window og sjekker bare om fokus står i et
 *   FELT; i en dialog står det på en knapp, så `j`, `k`, `n`, `x` og `/` ville byttet sak eller
 *   åpnet et skjema bak scrimet. Capture på window kjører før sidenes boble-lyttere, så
 *   `stopPropagation` her stopper dem. Tab, Enter og mellomrom slippes gjennom — de er
 *   dialogens egne.
 * - **Fokus tilbake** til elementet som hadde det, når dialogen lukkes.
 */
export function Dialog({ tittelId, onLukk, children, fokusFørst, testid, className }: { tittelId: string; onLukk: () => void; children: ReactNode; fokusFørst?: RefObject<HTMLElement | null>; testid?: string; className?: string }) {
  const rot = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const utløser = document.activeElement as HTMLElement | null;
    // Rekkefølge: kallerens ref, så et barn merket `data-fokus-forst` (Knapp fokusFørst — den
    // trygge knappen i en bekreftelse), så første fokuserbare, så dialogen selv.
    const første = fokusFørst?.current ?? rot.current?.querySelector<HTMLElement>('[data-fokus-forst]') ?? rot.current?.querySelector<HTMLElement>(FOKUSBAR) ?? rot.current;
    første?.focus();
    return () => {
      // Elementet kan være borte (raden er lagret og neste valgt); da er det ingenting å gå tilbake til.
      if (utløser && document.contains(utløser)) utløser.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onLukk();
        return;
      }
      if (e.key === 'Tab') {
        const fokuserbare = Array.from(rot.current?.querySelectorAll<HTMLElement>(FOKUSBAR) ?? []);
        if (fokuserbare.length === 0) return;
        const første = fokuserbare[0]!;
        const siste = fokuserbare[fokuserbare.length - 1]!;
        const aktivt = document.activeElement;
        if (e.shiftKey && (aktivt === første || !rot.current?.contains(aktivt))) {
          e.preventDefault();
          siste.focus();
        } else if (!e.shiftKey && (aktivt === siste || !rot.current?.contains(aktivt))) {
          e.preventDefault();
          første.focus();
        }
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') return;
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onLukk]);

  return (
    <div className="panel-scrim" role="presentation">
      <div ref={rot} role="dialog" aria-modal="true" aria-labelledby={tittelId} className={className ? `panel-dialog ${className}` : 'panel-dialog'} data-testid={testid} tabIndex={-1}>
        {children}
      </div>
    </div>
  );
}
